import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IItemCreationSubsectionInputProps,
  ItemCreationSubsection,
} from '~/Components/ItemCreation/ItemCreationSubsection';
import { ExtendedEventSeries } from '../../extendedEventSeries.model';
import { EventScheduleTimeSeriesEditorStyles } from './EventScheduleTimeSeriesEditor.styles';
import { useTimeSeriesDate, useTimeSeriesRecurrence } from './hooks';
import { EventType } from '~/Models/DTO/Event.model';
import { EventScheduleTimeSeriesDeleteSectionItem } from './EventScheduleTimeSeriesDeleteSectionItem';
import { useCheckEventSeriesBookings } from '../useCheckEventSeriesBookings';

export interface IEventScheduleTimeSeriesEditor {
  timeSeries: ExtendedEventSeries;
  onDelete: () => void;
  eventType: EventType;
}

export const EventScheduleTimeSeriesEditor = ({
  timeSeries,
  onDelete,
  eventType,
}: IEventScheduleTimeSeriesEditor) => {
  const { data: anyBookings } = useCheckEventSeriesBookings(timeSeries);
  const { data: futureBookings } = useCheckEventSeriesBookings(timeSeries, true);
  const hasAnyBookings = !!timeSeries.id && anyBookings?.[0].count !== 0;
  const hasFutureBookings = !!timeSeries.id && futureBookings?.[0].count !== 0;

  const { timeSeriesDate } = useTimeSeriesDate({
    timeSeries,
    eventType,
    hasAnyBookings,
    hasFutureBookings,
  });
  const { timeSeriesRecurrence } = useTimeSeriesRecurrence({ timeSeries, hasFutureBookings });
  const { classes } = EventScheduleTimeSeriesEditorStyles();
  const { t } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasTooltip = [EventType.FREE_TRAINING].includes(eventType);

  const itemDeleteSection: IItemCreationSubsectionInputProps<Partial<ExtendedEventSeries>>[] = [
    {
      customElement: <EventScheduleTimeSeriesDeleteSectionItem onDelete={onDelete} />,
    },
  ];

  return (
    <div data-testid="EventScheduleTimeSeriesEditor" className={classes.TimeSeriesEditorRoot}>
      <ItemCreationSubsection
        title={t('app.title.details')}
        isHeaderVisible={!isMobile || hasTooltip}
        items={timeSeriesDate}
        tooltip={
          hasTooltip && {
            title: t('event.freeTraining.timeHint'),
          }
        }
      />
      <ItemCreationSubsection
        title={t('time.recurrence')}
        isHeaderVisible
        items={timeSeriesRecurrence}
      />
      <ItemCreationSubsection
        title={t('time.recurrence')}
        isHeaderVisible={false}
        items={itemDeleteSection}
      />
    </div>
  );
};
